<template>
  <div class="flex flex-col pb-3">
    <div class="flex items-center justify-between flex-wrap gap-4">
      <div class="flex items-center flex-1">
        <div class="min-w-80">
          <div class="flex flex-row items-center">
            <slot />
            <div
              v-if="recording"
              class="mx-4 flex items-center"
            >
              <div class="w-4 h-4 rounded-full bg-red-500 mr-1" />
              <p>{{ timerDisplay }}</p>
            </div>
          </div>
          <p
            v-if="error"
            class="text-red-500 text-sm"
          >
            {{ error }}
          </p>
        </div>
      </div>
      <div class="flex items-center gap-3 flex-wrap">
        <base-button
          v-if="recording"
          class="font-bold text-lg"
          @click="togglePause"
        >
          <div class="flex items-center">
            <icon
              :name="isPaused ? 'mdi-play' : 'mdi-pause'"
              :size="20"
              class="mr-3"
              :color="'#000000'"
            />
            {{ isPaused ? 'Reprendre' : 'Pause' }}
          </div>
        </base-button>
        <InterviewRecordingButton
          :loading="loadingRecording"
          :recording="recording"
          @record="buttonPress"
          @import="onImportAudio"
        />
        <v-file-input
          v-show="false"
          id="input-file"
          accept="audio/*"
          @update:model-value="handleFileUpload"
        />
      </div>
      <microphone-configuration
        :visible="microphoneConfigurationVisible"
        @close="microphoneConfigurationVisible = false"
        @import-audio="onImportAudio"
        @finish="onConfigurationEnd"
      />
    </div>
    <audio-vizualizer
      v-show="recording && !isPaused"
      class="mt-2"
      :stream="stream"
    />
  </div>
</template>

<script setup lang="ts">
import {computed, onBeforeUnmount, ref} from 'vue';
import Icon from '@/core/components/icon/Icon.vue';
import BaseButton from '@/core/components/base/BaseButton.vue';
import InterviewAudioService from '@/interview/services/InterviewAudioService';
import MicrophoneConfiguration from '@/interview/components/microphone/MicrophoneConfiguration.vue';
import AudioVizualizer from '@/interview/components/microphone/AudioVizualizer.vue';
import {useInterviewStore} from '@/stores/interview-store';
import {storeToRefs} from 'pinia';
import InterviewRecordingButton from '@/interview/components/instance-modal/InterviewRecordingButton.vue';

const emit = defineEmits<{
    start: [];
    finish: [blob: Blob];
    finishUpload: [blob: Blob];
}>();

const interviewStore = useInterviewStore();
const { recording } = storeToRefs(interviewStore);

const recorder = ref<MediaRecorder | null>(null);
const audioChunks = ref<Blob[]>([]);
const startedAt = ref<Date | null>(null);
const timer = ref<number | null>(null);
const interval = ref<ReturnType<typeof setInterval> | null>(null);
const isPaused = ref(false);
const error = ref<string | null>(null);
const stream = ref<MediaStream | null>(null);
const microphoneConfigurationVisible = ref(false);
const loadingRecording = ref(false);

const timerDisplay = computed(() => {
    if (timer.value !== null) {
        const minutes = Math.floor(timer.value / 60);
        const seconds = timer.value % 60;

        const formattedMinutes = minutes < 10 ? `0${minutes}` : minutes;
        const formattedSeconds = seconds < 10 ? `0${seconds}` : seconds;

        return `${formattedMinutes}:${formattedSeconds}`;
    }

    return '';
});

/**
 * Start recording the audio
 */
const record = (deviceId: string): Promise<void> | undefined => {
    emit('start');
    isPaused.value = false;
    timer.value = 0;
    audioChunks.value = [];
    recorder.value = null;
    stream.value = null;

    const mediaConstraints: MediaStreamConstraints = {
        audio: {
            sampleRate: 44100,
            channelCount: 1,
        } as MediaTrackConstraints,
    };

    if (!deviceId) {
        microphoneConfigurationVisible.value = true;
        return;
    }

    (mediaConstraints.audio as MediaTrackConstraints).deviceId = { exact: deviceId };
    loadingRecording.value = true;

    return navigator.mediaDevices
        .getUserMedia(mediaConstraints)
        .then((mediaStream) => {
            if (!mediaStream?.active || !mediaStream.getAudioTracks().length) {
                throw new Error('Microphone access denied or not available');
            }

            stream.value = mediaStream;
            recorder.value = new MediaRecorder(mediaStream, {
                mimeType: InterviewAudioService.recordMimeType,
            });

            error.value = null;
            loadingRecording.value = false;
            startedAt.value = new Date();
            recorder.value.start();
            interviewStore.setRecording(true);

            interval.value = setInterval(updateTimeAgo, 1000);

            recorder.value.ondataavailable = (e: BlobEvent) => {
                audioChunks.value.push(e.data);
            };

            recorder.value.onstop = () => {
                const audioBlob = new Blob(audioChunks.value, {
                    type: InterviewAudioService.recordMimeType,
                });
                emit('finish', audioBlob);
                mediaStream.getTracks().forEach((track) => track.stop());
            };
        })
        .catch(() => {
            loadingRecording.value = false;
            microphoneConfigurationVisible.value = true;
            error.value = "Une erreur est survenue lors de l'accès au microphone";
        });
};

/**
 * Finish the recording
 */
const finish = (): void => {
    startedAt.value = null;
    interviewStore.setRecording(false);
    if (interval.value) {
        clearInterval(interval.value);
    }
    if (recorder.value) {
        recorder.value.stop();
    }
};

/**
 * Toggle the recording
 */
const buttonPress = (): void => {
    if (recording.value) {
        finish();
    } else {
        microphoneConfigurationVisible.value = true;
    }
};

/**
 * Update the timer
 */
const updateTimeAgo = (): void => {
    if (!startedAt.value) {
        return;
    }

    timer.value = timer.value !== null ? timer.value + 1 : 0;
};

/**
 * Pause or resume the recording
 */
const togglePause = (): void => {
    if (!recorder.value) return;

    if (isPaused.value) {
        recorder.value.resume();
        interval.value = setInterval(updateTimeAgo, 1000);
    } else {
        recorder.value.pause();
        if (interval.value) {
            clearInterval(interval.value);
        }
    }

    isPaused.value = !isPaused.value;
};

/**
 * Action when the import audio event is fired in the micro configuration.
 */
const onImportAudio = (): void => {
    microphoneConfigurationVisible.value = false;
    openFileUpload();
};

/**
 * Action when the micro configuration is finished.
 */
const onConfigurationEnd = (deviceId: string): void => {
    microphoneConfigurationVisible.value = false;
    record(deviceId);
};

/**
 * Open the file upload dialog
 */
const openFileUpload = (): void => {
    document.getElementById('input-file')?.click();
};

/**
 * Handle the file upload
 */
const handleFileUpload = (file: File | File[]): void => {
    const _file = Array.isArray(file) ? file : [file];
    const blob = new Blob(_file, { type: 'audio/mp3' });

    emit('finishUpload', blob);
};

onBeforeUnmount(() => {
    if (recorder.value && recorder.value.state !== 'inactive') {
        finish();
    }
});

defineExpose({
    finish
});
</script>
